/* Landing Product Picker — runtime logic.
 *
 * Auto-discovers every `.landing-picker` instance on the page and wires up
 * variant selection, Recharge selling-plan dropdown, dynamic pricing, and
 * add-to-cart that hooks into the standard Kismet header-cart-drawer flow.
 *
 * Per-block product data is read from <script type="application/json"
 * data-product-data="{block-id}"> tags rendered inline by the section.
 *
 * Multiple pickers per page are supported: each picker scopes its own
 * state and listeners to a single root element.
 */

(function () {
  if (window.__landingProductPickerInited) return;
  window.__landingProductPickerInited = true;

  function formatMoney(cents) {
    // Whole-dollar prices render without cents ($25, not $25.00).
    const n = cents / 100;
    return '$' + (Number.isInteger(n) ? n : n.toFixed(2));
  }

  // Parse weight + unit from a variant title. Supports both "4 lb bag"
  // (food → /lb) and "8 oz pack" (treats/toppers/nugs → /oz). Returns null
  // when no weight can be derived. Parsed from the title because variant.weight
  // is set inconsistently in this Shopify catalog.
  //
  // TODO(post-launch): extract to assets/utility.cost-per-unit.js exposing
  // window.KismetCostPerUnit.{parse, format} when a 2nd consumer appears
  // (PDP, target-sub-picker, featured-products-*, etc.). See the matching
  // TODO in sections/landing-product-picker.liquid.
  function parseWeightFromTitle(title) {
    if (!title) return null;
    var match = title.match(/(\d+(?:\.\d+)?)\s*(lb|oz)\b/i);
    if (!match) return null;
    return { value: parseFloat(match[1]), unit: match[2].toLowerCase() };
  }

  function initPicker(picker) {
    if (picker.dataset.pickerInited === '1') return;
    picker.dataset.pickerInited = '1';

    var productData = {};
    picker.querySelectorAll('script[data-product-data]').forEach(function (script) {
      var blockId = script.getAttribute('data-product-data');
      try {
        productData[blockId] = JSON.parse(script.textContent);
      } catch (e) {
        productData[blockId] = null;
      }
    });

    var selectedState = {};
    // Tracks which blocks have had their frequency dropdown touched by the
    // user, so a late-resolving fetch can preserve their selection instead
    // of resetting to the first plan.
    var userTouchedFreq = new Set();

    picker.querySelectorAll('.landing-picker__column').forEach(function (col) {
      var blockId = col.getAttribute('data-block-id');
      var sizeRadio = col.querySelector('[data-size-radio]:checked');
      var freqSelect = col.querySelector('[data-frequency-select]');
      var fallbackVariantId = null;
      if (productData[blockId] && productData[blockId].variants && productData[blockId].variants[0]) {
        fallbackVariantId = productData[blockId].variants[0].id;
      }
      selectedState[blockId] = {
        variantId: sizeRadio ? parseInt(sizeRadio.value) : fallbackVariantId,
        sellingPlanId: freqSelect ? parseInt(freqSelect.value) : null
      };
    });

    // Six shop-collection-row sections put 54 of these columns on
    // /collections, and each one fired its own /products/<handle>.js at load —
    // ~54 XHRs on the critical path for five rows most visitors never scroll
    // to. The card already renders from the inline data-product-data and is
    // built to fade in the live price when it arrives, so hydration can wait
    // until the picker is near the viewport.
    function hydrateColumns() {
      picker.querySelectorAll('.landing-picker__column').forEach(function (col) {
        var blockId = col.getAttribute('data-block-id');
        var handle = col.getAttribute('data-product-handle');
        if (!handle) return;

        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (product) {
            var allPlans = [];
            var allAllocationsMap = {};
            var availabilityMap = {};

            product.variants.forEach(function (v) {
              allAllocationsMap[v.id] = (v.selling_plan_allocations || []).map(function (a) {
                // next_order_price: the 2nd price adjustment (recurring
                // discount) when the plan has one — e.g. 30% first order,
                // 10% after. 0 when the plan has a single adjustment.
                var adj = a.price_adjustments || [];
                return {
                  selling_plan_id: a.selling_plan_id,
                  per_delivery_price: a.per_delivery_price,
                  next_order_price: adj.length > 1 && adj[1] && adj[1].price ? adj[1].price : 0
                };
              });
              availabilityMap[v.id] = v.available;
            });

            (product.selling_plan_groups || []).forEach(function (group) {
              (group.selling_plans || []).forEach(function (plan) {
                allPlans.push({ id: plan.id, name: plan.name });
              });
            });

            if (productData[blockId]) {
              productData[blockId].variants.forEach(function (v) {
                if (allAllocationsMap[v.id]) {
                  v.selling_plan_allocations = allAllocationsMap[v.id];
                }
                if (typeof availabilityMap[v.id] === 'boolean') {
                  v.available = availabilityMap[v.id];
                }
              });
              productData[blockId].selling_plans = allPlans;
            }

            var select = col.querySelector('[data-frequency-select]');
            if (select && allPlans.length) {
              // Preserve the select's CURRENT plan when it's still valid — a
              // user touch before this fetch resolved, or a programmatic
              // preselect (the quiz reveal sets the recommended cadence
              // before this picker script has even registered its listeners).
              // For untouched liquid-rendered cards select.value is the first
              // plan anyway, so this is a no-op there.
              var preserveId = userTouchedFreq.has(blockId)
                ? selectedState[blockId].sellingPlanId
                : (parseInt(select.value, 10) || null);
              var preservedExists = preserveId !== null && allPlans.some(function (p) { return p.id === preserveId; });

              select.innerHTML = '';
              allPlans.forEach(function (plan, i) {
                var opt = document.createElement('option');
                opt.value = plan.id;
                opt.textContent = plan.name;
                var shouldSelect = preservedExists ? plan.id === preserveId : i === 0;
                if (shouldSelect) opt.selected = true;
                select.appendChild(opt);
              });
              selectedState[blockId].sellingPlanId = preservedExists ? preserveId : allPlans[0].id;
            }

            updatePricing(blockId);
          });
      });
    }

    if (typeof IntersectionObserver !== 'function') {
      hydrateColumns();
    } else if (document.querySelector('script[data-kismet-product-schema]')) {
      // Product page: one picker, above the fold, and its live Recharge plan and
      // availability data is what the buy card and the sticky bar price off.
      // Deferring buys nothing there and would leave the money page's pricing
      // waiting on the viewport, so hydrate straight away.
      hydrateColumns();
    } else {
      // Generous margin so a fast scroller reaches a hydrated card, and the
      // first row (already on screen) hydrates on the observer's first callback.
      // Horizontal as well as vertical: the rows are scroll-snap carousels, so
      // a card is just as likely to be off-screen sideways as below.
      var hydrateObserver = new IntersectionObserver(function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          obs.disconnect();
          hydrateColumns();
          return;
        }
      }, { rootMargin: '800px 400px' });
      hydrateObserver.observe(picker);
    }

    function getVariant(blockId, variantId) {
      var data = productData[blockId];
      if (!data) return null;
      return data.variants.find(function (v) { return v.id === variantId; });
    }

    function getAllocation(variant, sellingPlanId) {
      if (!variant || !variant.selling_plan_allocations || !variant.selling_plan_allocations.length) return null;
      var match = variant.selling_plan_allocations.find(function (a) { return a.selling_plan_id === sellingPlanId; });
      return match || variant.selling_plan_allocations[0];
    }

    function updatePricing(blockId) {
      var state = selectedState[blockId];
      if (!state || !state.variantId) return;
      var column = picker.querySelector('[data-block-id="' + blockId + '"].landing-picker__column');
      if (!column) return;
      var variant = getVariant(blockId, state.variantId);
      if (!variant) return;
      var allocation = getAllocation(variant, state.sellingPlanId);
      if (allocation) state.sellingPlanId = allocation.selling_plan_id;

      var hasSubscription = column.getAttribute('data-has-subscription') === 'true';
      // Never fake a discount. If the allocation isn't loaded, show the
      // variant price as-is and hide the savings + one-time strikethrough.
      var hasRealDiscount = hasSubscription && allocation && allocation.per_delivery_price < variant.price;
      var subPrice = hasRealDiscount ? allocation.per_delivery_price : variant.price;
      var savings = hasRealDiscount
        ? Math.round((variant.price - subPrice) / variant.price * 100)
        : 0;

      // On-sale state (custom.sale_price metafield or compare_at). Drives the
      // strikethrough on non-subscription cards' main price + CTA.
      var hasVariantSale = variant.sale_price && variant.original_price && variant.sale_price < variant.original_price;

      // Subscribe price is always the discounted allocation when available.
      // For non-subscription products (e.g. Bundles) the main price is the
      // sale price when on sale, so the big price + CTA both reflect the
      // discount. Subscription one-time still has its own row + tertiary action.
      var displayPrice = subPrice;
      if (!hasSubscription && hasVariantSale) {
        displayPrice = variant.sale_price;
      }

      var subPriceEl = column.querySelector('[data-subscribe-price]');
      var subOriginalEl = column.querySelector('[data-subscribe-original-price]');
      var savingsEl = column.querySelector('[data-savings]');
      var onetimeWrapEl = column.querySelector('[data-onetime-wrap]');
      var onetimePriceEl = column.querySelector('[data-onetime-price]');

      if (subPriceEl) subPriceEl.textContent = formatMoney(displayPrice);
      // Non-subscription main-price strikethrough: show the original price
      // struck out beside the sale price, hide it when the variant isn't on sale.
      if (subOriginalEl) {
        if (!hasSubscription && hasVariantSale) {
          subOriginalEl.textContent = formatMoney(variant.original_price);
          subOriginalEl.removeAttribute('hidden');
        } else {
          subOriginalEl.setAttribute('hidden', '');
        }
      }
      if (savingsEl) {
        savingsEl.textContent = 'Save ' + savings + '%';
        if (savings > 0) { savingsEl.removeAttribute('hidden'); }
        else { savingsEl.setAttribute('hidden', ''); }
      }
      if (onetimeWrapEl) {
        if (hasRealDiscount) { onetimeWrapEl.removeAttribute('hidden'); }
        else { onetimeWrapEl.setAttribute('hidden', ''); }
      }
      // Top "One-time:" strikethrough anchors on the REGULAR/original one-time
      // price (struck to contrast with the subscribe price). The discounted
      // one-time price lives below the CTA. `onetimeShownPrice` (what the
      // customer actually pays one-time) is still forwarded in the detail.
      var onetimeShownPrice = variant.sale_price || variant.price;
      var onetimeStruckPrice = hasVariantSale ? variant.original_price : variant.price;
      if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(onetimeStruckPrice);

      // CTA label is fully derived from the live state — discount % +
      // current subscribe price for subscription products, or just
      // "Add to cart — $X" for one-time-only. Re-derived here on every
      // variant/frequency change so the button stays in sync with the
      // "Save X%" badge.
      var ctaBtn = column.querySelector('[data-subscribe-btn]');
      var ctaTextEl = column.querySelector('[data-cta-text]');
      if (ctaTextEl) {
        var ctaLabel;
        if (hasSubscription) {
          ctaLabel = (savings > 0 ? 'Subscribe & Save ' + savings + '%' : 'Subscribe & Save')
                   + ' — ' + formatMoney(displayPrice);
        } else {
          var baseOnetime = (ctaBtn && ctaBtn.getAttribute('data-cta-text')) || 'Add to cart';
          ctaLabel = baseOnetime + ' — ' + formatMoney(displayPrice);
        }
        ctaTextEl.textContent = ctaLabel;
      }

      // Tertiary "One-time purchase — $X" action below the CTA. When the
      // variant is on sale, shows the original strikethrough alongside the
      // sale price; otherwise just the regular variant price.
      var onetimeActionPriceEl = column.querySelector('[data-onetime-action-price]');
      var onetimeActionOriginalEl = column.querySelector('[data-onetime-action-original-price]');
      if (onetimeActionPriceEl) onetimeActionPriceEl.textContent = formatMoney(hasVariantSale ? variant.sale_price : variant.price);
      if (onetimeActionOriginalEl) {
        if (hasVariantSale) {
          onetimeActionOriginalEl.textContent = formatMoney(variant.original_price);
          onetimeActionOriginalEl.removeAttribute('hidden');
        } else {
          onetimeActionOriginalEl.setAttribute('hidden', '');
        }
      }

      var costPerLbEl = column.querySelector('[data-cost-per-lb]');
      if (costPerLbEl) {
        var weight = parseWeightFromTitle(variant.title);
        if (weight && weight.value > 0) {
          costPerLbEl.textContent = formatMoney(displayPrice / weight.value) + ' / ' + weight.unit;
          costPerLbEl.removeAttribute('hidden');
        } else {
          costPerLbEl.setAttribute('hidden', '');
        }
      }

      // Image link, and on shop cards the name/price link as well.
      Array.prototype.forEach.call(column.querySelectorAll('[data-image-link]'), function (link) {
        link.href = link.getAttribute('data-product-url') + '?variant=' + state.variantId;
      });

      // PDP buy card only: keep Shopify's standard variant signals in sync
      // with the selected variant. The custom picker drives its own state,
      // but page-context analytics/apps (GA4 view_item, dynamic remarketing,
      // identity tools) read the variant from ShopifyAnalytics.meta and the
      // `?variant=` URL param — which otherwise stay stuck on the default.
      // Gated by `data-pdp-wizard` so this only runs on the single-product
      // PDP buy card, never on landing pages with multiple product cards.
      if (column.hasAttribute('data-pdp-wizard') && state.variantId) {
        try {
          if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
            window.ShopifyAnalytics.meta.selectedVariantId = state.variantId;
          }
          var _u = new URL(window.location.href);
          if (_u.searchParams.get('variant') !== String(state.variantId)) {
            _u.searchParams.set('variant', state.variantId);
            window.history.replaceState({}, '', _u);
          }
        } catch (e) {}
      }

      // Notify external listeners (e.g. block.pdp-sticky-buy) that this
      // card's purchase state changed. No-op when nobody listens. The
      // detail surface is intentionally rich so consumers don't need to
      // re-derive prices/labels from variant data.
      var weightInfo = parseWeightFromTitle(variant.title);
      document.dispatchEvent(new CustomEvent('pdp:state-change', {
        detail: {
          blockId: blockId,
          variantId: state.variantId,
          variantTitle: variant.title,
          variantAvailable: variant.available,
          sellingPlanId: state.sellingPlanId,
          hasSubscription: hasSubscription,
          hasRealDiscount: hasRealDiscount,
          subscribePrice: subPrice,
          onetimePrice: variant.price,
          onetimeShownPrice: onetimeShownPrice,
          savingsPercent: savings,
          weightValue: weightInfo ? weightInfo.value : 0,
          weightUnit: weightInfo ? weightInfo.unit : '',
          costPerUnit: weightInfo && weightInfo.value > 0 ? displayPrice / weightInfo.value : 0,
          // Recurring (2nd+) order price for the plan; 0 when unknown/same.
          nextOrderPrice: hasRealDiscount && allocation && allocation.next_order_price ? allocation.next_order_price : 0
        }
      }));
    }

    picker.addEventListener('change', function (e) {
      var radio = e.target.closest('[data-size-radio]');
      if (radio) {
        var radioBlockId = radio.getAttribute('data-block-id');
        selectedState[radioBlockId].variantId = parseInt(radio.value);
        updatePricing(radioBlockId);
        return;
      }
      var select = e.target.closest('[data-frequency-select]');
      if (select) {
        var selectBlockId = select.getAttribute('data-block-id');
        if (!selectBlockId || !selectedState[selectBlockId]) return;
        userTouchedFreq.add(selectBlockId);
        selectedState[selectBlockId].sellingPlanId = parseInt(select.value);
        updatePricing(selectBlockId);
      }
    });

    // Shared add-to-cart for both the primary (Subscribe) button and the
    // tertiary (One-time purchase) link. The two only differ in whether
    // the body includes a `selling_plan` field.
    function addToCart(btn, includeSellingPlan) {
      var blockId = btn.getAttribute('data-block-id');
      var state = selectedState[blockId];
      if (!state || !state.variantId) return;
      // Don't POST a sold-out variant. UI shows the pill disabled, but the
      // previously-checked default could still be selected if it became
      // unavailable between SSR and click.
      var variant = getVariant(blockId, state.variantId);
      if (variant && variant.available === false) return;
      btn.style.opacity = '0.6';
      btn.style.pointerEvents = 'none';
      var body = { id: state.variantId, quantity: 1 };
      if (includeSellingPlan && state.sellingPlanId) {
        body.selling_plan = state.sellingPlanId;
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
        if (res.ok) {
          // Mirrors the PDP add-to-cart flow: dispatch the events the
          // header-cart-drawer custom element listens for so it refetches
          // the cart section and slides open. No checkout redirect.
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
        }
      }).catch(function () {
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
      });
    }

    picker.addEventListener('click', function (e) {
      // Tertiary one-time action — adds the variant to cart WITHOUT a
      // selling plan. Matched first because its data-onetime-btn is more
      // specific than data-subscribe-btn.
      var onetimeBtn = e.target.closest('[data-onetime-btn]');
      if (onetimeBtn) {
        e.preventDefault();
        addToCart(onetimeBtn, /* includeSellingPlan */ false);
        return;
      }

      // Primary Subscribe CTA — adds the variant with its selected
      // selling plan (subscription).
      var btn = e.target.closest('[data-subscribe-btn]');
      if (!btn) return;
      e.preventDefault();
      addToCart(btn, /* includeSellingPlan */ true);
    });
  }

  function initAll() {
    document.querySelectorAll('.landing-picker').forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: Shopify injects newly added/duplicated sections live without
  // a page reload and fires `shopify:section:load`. Initialize any picker
  // inside the loaded section so the editor preview works the same as a
  // fresh page load. The per-element `data-picker-inited` guard inside
  // initPicker prevents double-init when the same section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.landing-picker').forEach(initPicker);
  });

  // PDP flavor swap (block.pdp-buy-card.liquid) splices in a new product
  // section via AJAX without re-executing this external script. It fires
  // this event with the freshly inserted section so we bind a fresh picker
  // (size radios, freq, CTA) on it — otherwise the new buy card has no
  // change listener and the price stops updating. The per-element
  // `data-picker-inited` guard makes re-dispatch on existing DOM a no-op.
  document.addEventListener('kismet:product-picker:init', function (e) {
    var root = (e.detail && e.detail.root) || document;
    if (!root.querySelectorAll) return;
    root.querySelectorAll('.landing-picker').forEach(initPicker);
  });
})();
